import { Mention, MentionBlot } from 'quill-mention';
import Quill from 'quill/core';
import {
    AI_REFERENCE_BLOT_NAME,
    AI_REFERENCE_CLASS,
    AI_REFERENCE_ID_DATASET_KEY,
    AIReferenceBlotValue,
} from './ai-shared-delta-reference.util';

function isReferenceValue(data: unknown): data is AIReferenceBlotValue {
    return (
        typeof data === 'object' &&
        data !== null &&
        typeof (data as AIReferenceBlotValue).id === 'number' &&
        typeof (data as AIReferenceBlotValue).label === 'string'
    );
}

/**
 * Atomic chip blot for the in-text source references of an AI answer.
 * Subclasses quill-mention's MentionBlot to inherit the embed mechanics (Quill
 * strips foreign nodes, so the chip has to be a blot), and exposes the
 * reference id on the dataset: the click is handled by the balloon, which owns
 * the sources and the popover.
 */
export class AIReferenceBlot extends MentionBlot {
    static override blotName = AI_REFERENCE_BLOT_NAME;
    static override className = AI_REFERENCE_CLASS;

    static override create(data?: unknown): Node {
        // super.create() without data yields the bare embed node (with this
        // subclass' className) and skips the mention-specific children.
        const node = super.create() as Node;
        if (!isReferenceValue(data) || !(node instanceof HTMLElement)) {
            return node;
        }

        const label = document.createElement('span');
        label.className = `${AI_REFERENCE_CLASS}-label`;
        label.innerText = data.label;
        node.appendChild(label);

        node.dataset[AI_REFERENCE_ID_DATASET_KEY] = `${data.id}`;
        node.dataset['label'] = data.label;
        node.setAttribute('role', 'button');
        node.setAttribute('tabindex', '0');
        return node;
    }

    static override value(domNode: HTMLElement): AIReferenceBlotValue {
        return {
            id: +(domNode.dataset[AI_REFERENCE_ID_DATASET_KEY] ?? 0),
            label: domNode.dataset['label'] ?? '',
        };
    }
}

let registered = false;

export function registerAIReferenceQuill(): void {
    if (registered) {
        return;
    }
    registered = true;
    if (!Quill.imports['modules/mention']) {
        Quill.register({ 'modules/mention': Mention });
    }
    Quill.register({ [`blots/${AI_REFERENCE_BLOT_NAME}`]: AIReferenceBlot });
}
